import React, { useEffect, useMemo, useState } from 'react';
import { auth } from '../firebase';
import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';

type OobMode = 'resetPassword' | 'verifyEmail' | string;

interface OobActionHandlerProps {
  onClose: () => void;
}

const OobActionHandler: React.FC<OobActionHandlerProps> = ({ onClose }) => {
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const mode = (searchParams.get('mode') || '') as OobMode;
  const oobCode = searchParams.get('oobCode') || '';

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');

  useEffect(() => {
    const run = async () => {
      try {
        if (!oobCode) {
          throw new Error('Ongeldige of ontbrekende code.');
        }

        if (mode === 'verifyEmail') {
          await applyActionCode(auth, oobCode);
          setSuccess('✅ Je emailadres is succesvol geverifieerd.');
          setError('');
        } else if (mode === 'resetPassword') {
          const verifiedEmail = await verifyPasswordResetCode(auth, oobCode);
          setEmail(verifiedEmail);
          setError('');
        } else {
          setError('Onbekende actie.');
        }
      } catch (e: any) {
        setError(e?.message || 'Er is een fout opgetreden.');
      } finally {
        setLoading(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResetPassword = async () => {
    try {
      setError('');
      if (!newPassword || newPassword.length < 6) {
        setError('Wachtwoord moet minimaal 6 karakters bevatten.');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('Wachtwoorden komen niet overeen.');
        return;
      }
      await confirmPasswordReset(auth, oobCode, newPassword);
      setSuccess('✅ Je wachtwoord is succesvol gewijzigd. Je kunt nu inloggen.');
    } catch (e: any) {
      setError(e?.message || 'Wachtwoord wijzigen mislukt.');
    }
  };

  const closeAndCleanUrl = () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('mode');
      url.searchParams.delete('oobCode');
      url.searchParams.delete('apiKey');
      url.searchParams.delete('lang');
      url.searchParams.delete('continueUrl');
      window.history.replaceState({}, document.title, url.toString());
    } catch {}
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {mode === 'verifyEmail' ? 'Email Verifiëren' : mode === 'resetPassword' ? 'Wachtwoord Herstel' : 'Bevestiging'}
          </h2>
          <button className="text-gray-400 hover:text-gray-600 text-2xl" onClick={closeAndCleanUrl} aria-label="Sluiten">✕</button>
        </div>

        {loading && (
          <div className="text-gray-700">Bezig met laden...</div>
        )}

        {!loading && error && (
          <div className="mb-4 p-3 rounded-lg bg-red-100 border border-red-300 text-red-700 text-sm">{error}</div>
        )}

        {!loading && success && (
          <div className="mb-4 p-3 rounded-lg bg-green-100 border border-green-300 text-green-700 text-sm">{success}</div>
        )}

        {!loading && mode === 'verifyEmail' && (
          <div className="space-y-4">
            <p className="text-gray-700">Je email is geverifieerd. Je kunt dit venster sluiten.</p>
            <button onClick={closeAndCleanUrl} className="w-full bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors">Doorgaan</button>
          </div>
        )}

        {!loading && mode === 'resetPassword' && !success && (
          <div className="space-y-4">
            {email && (
              <p className="text-gray-700">Wachtwoord resetten voor: <span className="font-medium">{email}</span></p>
            )}
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Nieuw Wachtwoord</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-lg p-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500"
                placeholder="Minimaal 6 karakters"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Bevestig Wachtwoord</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-lg p-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500"
                placeholder="Herhaal wachtwoord"
              />
            </div>
            <div className="flex space-x-3">
              <button onClick={closeAndCleanUrl} className="flex-1 bg-gray-100 text-gray-700 font-semibold py-3 px-6 rounded-lg hover:bg-gray-200 transition-colors">Annuleren</button>
              <button onClick={handleResetPassword} className="flex-1 bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors">Wachtwoord wijzigen</button>
            </div>
          </div>
        )}

        {!loading && mode === 'resetPassword' && success && (
          <div className="space-y-4">
            <button onClick={closeAndCleanUrl} className="w-full bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors">Inloggen</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OobActionHandler;


